import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ClientSidebar from '../components/ClientSidebar';
import NotificationBell from '../components/NotificationBell';
import ChatBotTrigger from '../../components/ChatBotTrigger';

import { API_BASE as API_BASE_URL } from '../../config/runtime';

const getStoredClientUser = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed?.role || '').toLowerCase() === 'client' ? parsed : null;
  } catch {
    return null;
  }
};

const ClientProfile = () => {
  const navigate = useNavigate();
  const clientUser = getStoredClientUser();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showScrollTop, setShowScrollTop] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
  });

  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: '',
    booking_count: 0,
    member_since: '',
    branch_name: '',
  });

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!clientUser?.user_id) {
      setLoading(false);
      setMessage({ type: 'error', text: 'User not logged in' });
      return;
    }

    const fetchProfileData = async () => {
      try {
        setLoading(true);
        
        // Fetch user profile data
        const userResponse = await fetch(`${API_BASE_URL}/get_users.php?user_id=${clientUser.user_id}`);
        const userData = await userResponse.json();

        if (userData.success) {
          const user = userData.user;
          setFormData({
            full_name: user.name || '',
            email: user.email || '',
            phone: user.contact || '',
          });

          let memberSince = '';
          if (user.created_at) {
            const date = new Date(user.created_at);
            memberSince = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          }

          // Fetch client bookings to count approved and completed
          try {
            const bookingsResponse = await fetch(`${API_BASE_URL}/get_client_bookings.php?user_id=${clientUser.user_id}`);
            const bookingsData = await bookingsResponse.json();
            
            let approvedAndCompletedCount = 0;
            if (bookingsData.success && Array.isArray(bookingsData.bookings)) {
              approvedAndCompletedCount = bookingsData.bookings.filter(
                booking => booking.status === 'Approved' || booking.status === 'Completed'
              ).length;
            }

            setProfileData({
              full_name: user.name || '',
              email: user.email || '',
              phone: user.contact || '',
              booking_count: approvedAndCompletedCount,
              member_since: memberSince,
              branch_name: user.branch_name || '',
            });
          } catch (bookingsError) {
            console.error('Error fetching bookings:', bookingsError);
            setProfileData({
              full_name: user.name || '',
              email: user.email || '',
              phone: user.contact || '',
              booking_count: userData.booking_count || 0,
              member_since: memberSince,
              branch_name: user.branch_name || '',
            });
          }
        } else {
          setMessage({ type: 'error', text: userData.message || 'Failed to load profile data' });
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        setMessage({ type: 'error', text: 'Network error. Please check your connection.' });
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [clientUser?.user_id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!clientUser?.user_id) {
      setMessage({ type: 'error', text: 'User not logged in' });
      return;
    }

    if (!formData.full_name.trim() || !formData.email.trim()) {
      setMessage({ type: 'error', text: 'Full name and email are required' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const response = await fetch(`${API_BASE_URL}/update_user.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: clientUser.user_id,
          name: formData.full_name.trim(),
          email: formData.email.trim(),
          contact: formData.phone.trim(),
          branch_id: clientUser.branch_id || 1,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setIsEditing(false);

        const updatedUser = {
          ...clientUser,
          full_name: formData.full_name,
          email: formData.email,
          contact: formData.phone,
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));

        const refreshResponse = await fetch(`${API_BASE_URL}/get_users.php?user_id=${clientUser.user_id}`);
        const refreshData = await refreshResponse.json();

        if (refreshData.success) {
          const user = refreshData.user;
          setFormData({
            full_name: user.name || '',
            email: user.email || '',
            phone: user.contact || '',
          });

          let memberSince = '';
          if (user.created_at) {
            const date = new Date(user.created_at);
            memberSince = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          }

          setProfileData({
            full_name: user.name || '',
            email: user.email || '',
            phone: user.contact || '',
            booking_count: refreshData.booking_count || 0,
            member_since: memberSince,
            branch_name: user.branch_name || '',
          });
        }
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage({ type: 'error', text: 'Network error. Please check your connection.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      full_name: profileData.full_name || clientUser?.full_name || '',
      email: profileData.email || clientUser?.email || '',
      phone: profileData.phone || '',
    });
    setIsEditing(false);
    setMessage({ type: '', text: '' });
  };

  const handleViewBookings = () => {
    navigate('/history');
  };

  if (loading) {
    return (
      <div className="min-h-screen hygea-app-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#b37b65] mx-auto mb-4"></div>
          <p className="text-gray-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hygea-app-bg">
      {/* Notification Bell */}
      <div className="fixed top-4 right-4 sm:right-6 z-50">
        <NotificationBell isScrolled={false} forceBrown={true} />
      </div>

      <ClientSidebar
        isOpen={isSidebarOpen}
        onOpenChange={setIsSidebarOpen}
        clientName={clientUser?.full_name}
        clientEmail={clientUser?.email}
        clientAvatar={clientUser?.avatar_url}
        onLogout={() => { window.location.href = '/'; }}
      />

      <div className="transition-all duration-300">
        <div className="px-4 py-6 sm:p-8">
          <div className="max-w-4xl mx-auto">

            {/* Header */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">My Profile</h2>
              <p className="text-gray-500 mt-1 text-sm sm:text-base">Manage your personal information</p>
            </div>

            {/* Message Alert */}
            {message.text && (
              <div className={`mb-5 p-4 rounded-xl text-sm ${
                message.type === 'success'
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                  : 'bg-rose-50 border border-rose-200 text-rose-700'
              }`}>
                <p className="font-medium">{message.text}</p>
              </div>
            )}

            {/* Profile Header Card */}
            <div className="bg-gradient-to-r from-[#b37b65] to-[#6f4a42] rounded-2xl p-5 sm:p-8 text-white mb-5 sm:mb-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 text-center sm:text-left">
                {/* Avatar */}
                <div className="h-20 w-20 sm:h-24 sm:w-24 shrink-0 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  {clientUser?.avatar_url ? (
                    <img
                      src={clientUser.avatar_url}
                      alt="Profile"
                      className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl sm:text-4xl font-bold text-white">
                      {formData.full_name ? formData.full_name.charAt(0).toUpperCase() : '?'}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold">{formData.full_name || 'Loading...'}</h3>
                  <p className="text-white/80 text-sm sm:text-base">{formData.email || 'Loading...'}</p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-5 sm:mb-6">
              <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-200">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Member Since</p>
                <p className="text-base sm:text-lg font-bold text-gray-800 mt-1">{profileData.member_since || 'N/A'}</p>
              </div>
              <button
                onClick={handleViewBookings}
                className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-200 hover:border-[#b37b65] hover:shadow-md transition-all cursor-pointer text-left"
              >
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Total Bookings</p>
                <p className="text-2xl sm:text-3xl font-bold text-[#b37b65] mt-2">{profileData.booking_count}</p>
                <p className="text-xs text-gray-500 mt-2">Click to view all bookings</p>
              </button>
            </div>

            {/* Personal Information */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-8">
              {/* Card Header */}
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800">Personal Information</h3>
                  {!isEditing ? (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-[#b37b65] text-white rounded-xl font-bold text-sm hover:bg-[#996758] active:scale-95 transition-all"
                    >
                      Edit Profile
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={handleCancel}
                        disabled={saving}
                        className="flex-1 sm:flex-none px-4 py-2.5 sm:py-2 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 active:scale-95 transition-all disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex-1 sm:flex-none px-4 py-2.5 sm:py-2 bg-emerald-500 text-white rounded-xl font-bold text-sm hover:bg-emerald-600 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {saving ? (
                          <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Saving...
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Form Fields — 1 col on mobile, 2 col on sm+ */}
              <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
                    Full Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#b37b65] outline-none transition text-sm"
                    />
                  ) : (
                    <p className="text-gray-800 font-medium text-sm sm:text-base">{formData.full_name || '—'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
                    Email Address
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#b37b65] outline-none transition text-sm"
                    />
                  ) : (
                    <p className="text-gray-800 font-medium text-sm sm:text-base break-all">{formData.email || '—'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
                    Phone Number
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#b37b65] outline-none transition text-sm"
                    />
                  ) : (
                    <p className="text-gray-800 font-medium text-sm sm:text-base">{formData.phone || '—'}</p>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-4 sm:right-6 z-50 w-10 h-10 bg-[#b37b65] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#996758] active:scale-95 transition-all"
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
      <ChatBotTrigger />
    </div>
  );
};

export default ClientProfile;
